import React, { useContext, useMemo } from "react";
import { MapGuiContext } from "~/Context/MapGuiContext";
import { getNestedValue } from "~/js/utilities/getNestedValue";
import { formatGpxValue } from "~/js/Views/Track/GPX/gpxFormatter";
import { useTranslation } from "react-i18next";
import { GPXDATA_KEYVALUES_MAPPING } from "~/js/config/GPXMapping";
import Icon from "Trackalyze/Icon";
import Typo from "Trackalyze/Typo";
import Flex from "Trackalyze/Flex";
import SettingsContext from "~/Context/SettingsContext";
import GPXIcon from "./GPXIcon";
import { ListItem } from "Trackalyze/List";

const GPXData = (props) => {
  const { regional, units } = useContext(SettingsContext);

  const { trackData } = useContext(MapGuiContext);
  const { t } = useTranslation();

  const gpxItems = useMemo(() => {
    const { type, id, value, count } = props;
    const statistics = trackData?.stats;
    const items = [];

    if (!statistics) {
      return items;
    }

    const processItem = (type, id, data) => {
      const item = { type, id };
      const dataInfo = getNestedValue(GPXDATA_KEYVALUES_MAPPING, type, id);
      item.label = dataInfo?.label ? t(dataInfo.label) : "-";
      item.symbol = dataInfo?.symbol || "";

      if (data !== null && data !== undefined) {
        const formattedData = formatGpxValue(
          data,
          type,
          units.metric,
          regional.countrycode,
          trackData?.info?.type,
        );
        item.unit = formattedData.unit;
        item.value = formattedData.value;
      }

      return item.value ? item : null;
    };

    if (id) {
      // If value is provided directly, use it. Otherwise try to get it from statistics.
      // If value is provided, we still need to process it to get unit/formatting.
      const data =
        value !== undefined ? value : getNestedValue(statistics, type, id);

      // If we have a direct value, we might not have statistics context, but we still want to format it.
      // However, if statistics is missing and value is NOT provided, we return empty.
      if (data === undefined && !statistics) return items;

      const item = processItem(type || "elevation", id, data);
      if (item) {
        if (
          item.value === "NaN" ||
          item.value === undefined ||
          item.value === 0
        ) {
          item.value = null;
        }
        items.push(item);
      }
    } else if (type) {
      if (statistics && type in statistics) {
        const typeStats = statistics[type];
        // Get the keys from GPXDATA_MAPPING to preserve order
        let itemKeys = Object.keys(GPXDATA_KEYVALUES_MAPPING[type]);
        if (count) {
          itemKeys = itemKeys.slice(0, count);
        }

        for (const statId of itemKeys) {
          const data = typeStats[statId];
          const item = processItem(type, statId, data);
          if (item) {
            if (
              item.value === "NaN" ||
              item.value === undefined ||
              item.value === "0" ||
              item.value === 0
            ) {
              item.value = null;
            }
            items.push(item);
          }
        }
      }
    }
    return items;
  }, [trackData, props.type, props.id, props.value, props.count, t]);

  if (gpxItems.length === 0) return null;

  const isSummary = !props.variant || props.variant === "summary";
  const isDetail = props.variant === "detail";
  const isTest = props.variant === "test";
  const isList = props.variant === "list";
  const isQuickinfo = props.variant === "quickinfo";
  const isText = props.variant === "text";
  const isSplits = props.variant === "splits";

  return (
    <>
      {gpxItems.map((gpxItem) =>
        gpxItem.value !== null && gpxItem.value !== "0" ? (
          <React.Fragment key={`${gpxItem.type}-${gpxItem.id}`}>
            {isTest && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="test"
                className="gpx_item"
              >
                <GPXIcon
                  type={gpxItem.type}
                  id={gpxItem.id}
                  symbol={gpxItem.symbol}
                  size="l"
                />
              </div>
            )}
            {isSummary && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="summary"
                data-gpx-outline={gpxItem.type}
                className="gpx_item ta-flexitem"
              >
                <Flex spacing="3" itemscenter>
                  {!props.noSymbol && <Icon symbol={gpxItem.symbol} size="l" />}
                  <Flex vertical>
                    <Flex spacing="1" itemsend>
                      <Typo
                        sHigh
                        type="title"
                        size="m"
                        className="ta-color-text_on-surface"
                      >
                        {gpxItem.value}
                      </Typo>
                      <Typo
                        type="label"
                        size="m"
                        className="ta-color-text_on-surface-variant"
                      >
                        {gpxItem.unit}
                      </Typo>
                    </Flex>
                    <Typo
                      type="label"
                      size="m"
                      sHigh
                      className="ta-color-text_on-surface-lowest"
                    >
                      {gpxItem.label}
                    </Typo>
                  </Flex>
                </Flex>
              </div>
            )}

            {isList && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="summary"
                className="gpx_item ta-flexitem"
              >
                <ListItem
                  className="ta-flexitem ta-padding_top-0"
                  label={gpxItem.label}
                  trailing={
                    <Flex
                      spacing="1"
                      itemsend
                      className="ta-color-text_on-surface-low"
                    >
                      <Typo type="title" size="s" noWrap>
                        {gpxItem.value}
                      </Typo>
                      <Typo type="title" size="s">
                        {gpxItem.unit}
                      </Typo>
                    </Flex>
                  }
                />
              </div>
            )}
            {isDetail && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="detail"
                className="gpx_item"
              >
                <Icon symbol={gpxItem.symbol} size="l" />
                <Flex center vertical>
                  <Typo
                    type="label"
                    size="m"
                    noWrap
                    className="ta-color-text_on-surface-lowest"
                  >
                    {gpxItem.label}
                  </Typo>
                  <Flex center spacing="1">
                    <Typo type="title" size="m" className="ta-no-wrap">
                      {gpxItem.value}
                    </Typo>
                    {gpxItem.unit !== "" && (
                      <Typo
                        type="title"
                        size="m"
                        className="ta-color-text_on-surface-low"
                      >
                        {gpxItem.unit}
                      </Typo>
                    )}
                  </Flex>
                </Flex>
              </div>
            )}
            {isQuickinfo && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="quickinfo"
                className="gpx_item"
              >
                {!props.noSymbol && <Icon symbol={gpxItem.symbol} size="s" />}
                <Flex itemscenter spacing="1">
                  <Typo
                    type="label"
                    size="s"
                    className="ta-no-wrap ta-color-text_on-surface"
                  >
                    {gpxItem.value}
                  </Typo>
                  <Typo
                    type="label"
                    size="s"
                    className="ta-color-text_on-surface-variant"
                  >
                    {gpxItem.unit}
                  </Typo>
                </Flex>
              </div>
            )}
            {isText && (
              <div
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="text"
                className="gpx_item"
              >
                {gpxItem.value} {gpxItem.unit}
              </div>
            )}
            {isSplits && (
              <span
                data-gpx-type={gpxItem.type}
                data-gpx-id={gpxItem.id}
                data-gpx-variant="stops"
                className="gpx_item"
              >
                <Typo type="label" size="s" className="ta-no-wrap">
                  {gpxItem.value}
                </Typo>
              </span>
            )}
          </React.Fragment>
        ) : null,
      )}
    </>
  );
};

export default GPXData;
